import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Search, Filter, Clock, CheckCircle, XCircle, 
  AlertCircle, User, Building2, TrendingUp, Wallet, FileText,
  Mail, Phone, Globe, Linkedin, Calendar, ChevronDown, ChevronUp,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: 'amber', icon: Clock },
  approved: { label: 'Approved', color: 'green', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'red', icon: XCircle },
  needs_info: { label: 'Needs Info', color: 'blue', icon: AlertCircle },
};

const AdminOnboardingReview = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    founders: true,
    company: false,
    business: false,
    funding: false,
    documents: false,
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/onboarding-applications`, {
        withCredentials: true
      });
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (referenceNumber, newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(
        `${API}/admin/onboarding-applications/${referenceNumber}?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Application ${newStatus.replace('_', ' ')}`);
      loadApplications();
      if (selectedApp?.reference_number === referenceNumber) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.founderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: applications.length,
    pending_review: applications.filter(a => a.status === 'pending_review').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
    needs_info: applications.filter(a => a.status === 'needs_info').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  const renderDetailSection = (title, icon, sectionKey, content) => {
    const Icon = icon;
    const isExpanded = expandedSections[sectionKey];
    
    return (
      <div className="border border-slate-700 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between p-4 bg-slate-800/50 hover:bg-slate-800 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5 text-cyan-400" />
            <span className="font-medium text-slate-100">{title}</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </button>
        {isExpanded && (
          <div className="p-4 bg-slate-900/50 space-y-3">
            {content}
          </div>
        )}
      </div>
    );
  };

  const DetailRow = ({ label, value, isLink }) => (
    <div className="flex justify-between py-1">
      <span className="text-slate-400 text-sm">{label}</span>
      {isLink && value ? (
        <a 
          href={value.startsWith('http') ? value : `https://${value}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-cyan-400 text-sm hover:underline flex items-center"
        >
          {value.length > 40 ? value.substring(0, 40) + '...' : value}
          <ExternalLink className="w-3 h-3 ml-1" />
        </a>
      ) : (
        <span className="text-slate-200 text-sm text-right max-w-[60%]">{value || '—'}</span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="admin-onboarding-review">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-slate-400 hover:text-slate-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <h1 className="text-xl font-bold text-slate-100">Onboarding Applications</h1>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-400">{user.name}</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Left Panel - Application List */}
          <div className="w-1/3 space-y-4">
            {/* Search & Filter */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by company, founder, or reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-slate-100"
                  data-testid="search-input"
                />
              </div>
              
              {/* Status Tabs */}
              <div className="flex flex-wrap gap-2">
                {['all', 'pending_review', 'approved', 'rejected', 'needs_info'].map((status) => {
                  const config = STATUS_CONFIG[status];
                  const count = statusCounts[status];
                  
                  return (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        statusFilter === status
                          ? status === 'all'
                            ? 'bg-slate-600 text-white'
                            : `bg-${config?.color || 'slate'}-500/20 text-${config?.color || 'slate'}-400 border border-${config?.color || 'slate'}-500/30`
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                      data-testid={`filter-${status}`}
                    >
                      {status === 'all' ? 'All' : config?.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Application List */}
            <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
              {filteredApplications.length === 0 ? (
                <Card className="bg-slate-800/40 border-slate-700 p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-400">No applications found</p>
                </Card>
              ) : (
                filteredApplications.map((app) => {
                  const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending_review;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <Card
                      key={app.reference_number}
                      onClick={() => setSelectedApp(app)}
                      className={`bg-slate-800/40 border-slate-700 p-4 cursor-pointer transition-all ${
                        selectedApp?.reference_number === app.reference_number
                          ? 'border-cyan-500 shadow-lg shadow-cyan-500/10'
                          : 'hover:border-slate-600'
                      }`}
                      data-testid={`app-${app.reference_number}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-slate-100">{app.companyName}</h3>
                          <p className="text-sm text-slate-400">{app.founderName}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs flex items-center space-x-1 bg-${statusConfig.color}-500/10 text-${statusConfig.color}-400 border border-${statusConfig.color}-500/20`}>
                          <StatusIcon className="w-3 h-3" />
                          <span>{statusConfig.label}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{app.primarySector}</span>
                        <span>{app.reference_number}</span>
                      </div>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-slate-400">
                        <span>{app.currentRound}</span>
                        <span>{app.totalRoundSize}</span>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Panel - Application Details */}
          <div className="flex-1">
            {selectedApp ? (
              <Card className="bg-slate-800/40 border-slate-700 p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-6 pb-6 border-b border-slate-700">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-100">{selectedApp.companyName}</h2>
                    <p className="text-slate-400">{selectedApp.brandName && selectedApp.brandName !== selectedApp.companyName ? `(${selectedApp.brandName})` : ''}</p>
                    <p className="text-sm text-cyan-400 font-mono mt-1">{selectedApp.reference_number}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Submitted: {new Date(selectedApp.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    {(() => {
                      const config = STATUS_CONFIG[selectedApp.status] || STATUS_CONFIG.pending_review;
                      const Icon = config.icon;
                      return (
                        <span className={`px-3 py-1.5 rounded-full text-sm flex items-center space-x-2 bg-${config.color}-500/10 text-${config.color}-400 border border-${config.color}-500/20`}>
                          <Icon className="w-4 h-4" />
                          <span>{config.label}</span>
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3 mb-6">
                  <Button
                    onClick={() => updateStatus(selectedApp.reference_number, 'approved')}
                    disabled={updating || selectedApp.status === 'approved'}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedApp.reference_number, 'rejected')}
                    disabled={updating || selectedApp.status === 'rejected'}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedApp.reference_number, 'needs_info')}
                    disabled={updating || selectedApp.status === 'needs_info'}
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    data-testid="needs-info-btn"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Request Info
                  </Button>
                </div>

                {/* Detail Sections */}
                <div className="space-y-3">
                  {renderDetailSection('Founders & Team', User, 'founders', (
                    <>
                      <DetailRow label="Primary Founder" value={selectedApp.founderName} />
                      <DetailRow label="Designation" value={selectedApp.founderDesignation} />
                      <DetailRow label="Email" value={selectedApp.founderEmail} />
                      <DetailRow label="Mobile" value={selectedApp.founderMobile} />
                      <DetailRow label="LinkedIn" value={selectedApp.founderLinkedIn} isLink />
                      <DetailRow label="Experience" value={selectedApp.founderExperience} />
                      <DetailRow label="Education" value={selectedApp.founderEducation} />
                      {selectedApp.founderBio && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Bio</p>
                          <p className="text-sm text-slate-300">{selectedApp.founderBio}</p>
                        </div>
                      )}
                      {selectedApp.coFounder2Name && (
                        <DetailRow label="Co-Founder 2" value={`${selectedApp.coFounder2Name} (${selectedApp.coFounder2Role})`} />
                      )}
                      {selectedApp.coFounder3Name && (
                        <DetailRow label="Co-Founder 3" value={`${selectedApp.coFounder3Name} (${selectedApp.coFounder3Role})`} />
                      )}
                      <DetailRow label="Team Size" value={selectedApp.teamSize} />
                      <DetailRow label="Advisors" value={selectedApp.advisors} />
                    </>
                  ))}

                  {renderDetailSection('Company & Structure', Building2, 'company', (
                    <>
                      <DetailRow label="Company Name" value={selectedApp.companyName} />
                      <DetailRow label="Brand Name" value={selectedApp.brandName} />
                      <DetailRow label="Website" value={selectedApp.website} isLink />
                      <DetailRow label="Entity Type" value={selectedApp.entityType} />
                      <DetailRow label="Country" value={selectedApp.countryOfIncorporation} />
                      <DetailRow label="CIN" value={selectedApp.cin} />
                      <DetailRow label="Year Founded" value={selectedApp.yearFounded} />
                      <DetailRow label="Registered Address" value={selectedApp.registeredAddress} />
                      <DetailRow label="Operating City" value={selectedApp.operatingCity} />
                      <DetailRow label="Operating Countries" value={selectedApp.operatingCountries} />
                      <DetailRow label="Founders' Ownership" value={selectedApp.foundersOwnership} />
                      <DetailRow label="ESOP Pool" value={selectedApp.esopPool} />
                      {selectedApp.existingInvestors && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Existing Investors</p>
                          <p className="text-sm text-slate-300">{selectedApp.existingInvestors}</p>
                        </div>
                      )}
                    </>
                  ))}

                  {renderDetailSection('Business & Traction', TrendingUp, 'business', (
                    <>
                      <DetailRow label="Sector" value={selectedApp.primarySector} />
                      <DetailRow label="One-liner" value={selectedApp.oneLineDescription} />
                      <DetailRow label="Business Model" value={selectedApp.businessModel} />
                      <DetailRow label="Target Customer" value={selectedApp.targetCustomer} />
                      <DetailRow label="Current Stage" value={selectedApp.currentStage} />
                      <div className="grid grid-cols-2 gap-2 mt-3 p-3 bg-slate-800/50 rounded-lg">
                        <DetailRow label="Monthly Revenue" value={selectedApp.monthlyRevenue} />
                        <DetailRow label="Revenue Growth" value={selectedApp.revenueGrowth} />
                        <DetailRow label="Gross Margin" value={selectedApp.grossMargin} />
                        <DetailRow label="Total Customers" value={selectedApp.totalCustomers} />
                        <DetailRow label="CAC" value={selectedApp.cac} />
                        <DetailRow label="LTV" value={selectedApp.ltv} />
                        <DetailRow label="Monthly Burn" value={selectedApp.monthlyBurn} />
                        <DetailRow label="Runway" value={selectedApp.runway} />
                      </div>
                      {selectedApp.problemSolution && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Problem & Solution</p>
                          <p className="text-sm text-slate-300">{selectedApp.problemSolution}</p>
                        </div>
                      )}
                      {selectedApp.keyMilestones && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Key Milestones</p>
                          <p className="text-sm text-slate-300">{selectedApp.keyMilestones}</p>
                        </div>
                      )}
                      {selectedApp.competitiveLandscape && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Competitive Landscape</p>
                          <p className="text-sm text-slate-300">{selectedApp.competitiveLandscape}</p>
                        </div>
                      )}
                    </>
                  ))}

                  {renderDetailSection('Funding Ask', Wallet, 'funding', (
                    <>
                      <div className="grid grid-cols-2 gap-2 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg mb-3">
                        <DetailRow label="Current Round" value={selectedApp.currentRound} />
                        <DetailRow label="Round Size" value={selectedApp.totalRoundSize} />
                        <DetailRow label="Pre-Money Valuation" value={selectedApp.preMoneyValuation} />
                        <DetailRow label="Instrument" value={selectedApp.instrument} />
                      </div>
                      <DetailRow label="Amount Raised" value={selectedApp.amountRaised} />
                      <DetailRow label="Amount Seeking" value={selectedApp.amountSeeking} />
                      <DetailRow label="Min Ticket Size" value={selectedApp.minTicketSize} />
                      <DetailRow label="Target Close Date" value={selectedApp.targetCloseDate} />
                      <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
                        <p className="text-xs text-slate-400 mb-2">Use of Funds</p>
                        <div className="space-y-1">
                          {selectedApp.useOfFundsProduct && <DetailRow label="Product & Tech" value={`${selectedApp.useOfFundsProduct}%`} />}
                          {selectedApp.useOfFundsSales && <DetailRow label="Sales & Marketing" value={`${selectedApp.useOfFundsSales}%`} />}
                          {selectedApp.useOfFundsTeam && <DetailRow label="Team & Hiring" value={`${selectedApp.useOfFundsTeam}%`} />}
                          {selectedApp.useOfFundsOperations && <DetailRow label="Operations" value={`${selectedApp.useOfFundsOperations}%`} />}
                          {selectedApp.useOfFundsOther && <DetailRow label="Other" value={`${selectedApp.useOfFundsOther}%`} />}
                        </div>
                      </div>
                      {selectedApp.useOfFundsNarrative && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Use of Funds Narrative</p>
                          <p className="text-sm text-slate-300">{selectedApp.useOfFundsNarrative}</p>
                        </div>
                      )}
                      <DetailRow label="Previous Funding" value={selectedApp.previousFunding} />
                      <DetailRow label="Previous Investors" value={selectedApp.previousInvestors} />
                      {selectedApp.openToStrategic && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Strategic Investors Interest</p>
                          <p className="text-sm text-slate-300">{selectedApp.openToStrategic}</p>
                        </div>
                      )}
                    </>
                  ))}

                  {renderDetailSection('Documents & Data Room', FileText, 'documents', (
                    <>
                      <DetailRow label="Pitch Deck" value={selectedApp.pitchDeckUrl} isLink />
                      <DetailRow label="Financial Model" value={selectedApp.financialModelUrl} isLink />
                      <DetailRow label="Data Room" value={selectedApp.dataRoomUrl} isLink />
                      <DetailRow label="Product Demo" value={selectedApp.productDemoUrl} isLink />
                      <DetailRow label="IP Status" value={selectedApp.ipStatus} />
                      <DetailRow label="Regulatory Approvals" value={selectedApp.regulatoryApprovals} />
                      {selectedApp.legalIssues && (
                        <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <p className="text-xs text-red-400 mb-1">Legal Issues</p>
                          <p className="text-sm text-slate-300">{selectedApp.legalIssues}</p>
                        </div>
                      )}
                      {selectedApp.additionalInfo && (
                        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Additional Info</p>
                          <p className="text-sm text-slate-300">{selectedApp.additionalInfo}</p>
                        </div>
                      )}
                    </>
                  ))}
                </div>
              </Card>
            ) : (
              <Card className="bg-slate-800/40 border-slate-700 p-12 text-center h-full flex items-center justify-center">
                <div>
                  <FileText className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-lg font-semibold text-slate-300 mb-2">Select an Application</h3>
                  <p className="text-slate-400">Click on an application to view details and take action</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOnboardingReview;
